import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

// خريطة الحروف للمعاينة
const EN_TO_AR_LETTER = {
  'A': 'ا', 'B': 'ب', 'J': 'ح', 'D': 'د', 'R': 'ر',
  'S': 'س', 'T': 'ط', 'E': 'ع', 'G': 'ق', 'K': 'ك',
  'L': 'ل', 'M': 'م', 'N': 'ن', 'H': 'ه', 'U': 'و', 'V': 'ي'
};

const VSMVehicles = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const T = {
    title:           { ar: '🚙 السيارات', en: '🚙 Vehicles' },
    subtitle:        { ar: 'سيارة', en: 'vehicle(s)' },
    add:             { ar: '+ إضافة سيارة', en: '+ Add Vehicle' },
    edit:            { ar: '✏️ تعديل بيانات السيارة', en: '✏️ Edit Vehicle' },
    create:          { ar: '➕ سيارة جديدة', en: '➕ New Vehicle' },
    search:          { ar: 'ابحث بالرقم أو الموديل...', en: 'Search by plate or model...' },
    client:          { ar: 'العميل', en: 'Client' },
    selectClient:    { ar: 'ابحث واختر العميل...', en: 'Search and select client...' },
    noClientFound:   { ar: 'لا يوجد عملاء', en: 'No clients found' },
    plateInfo:       { ar: 'رقم اللوحة', en: 'License Plate' },
    plateInput:      { ar: 'اكتب رقم اللوحة', en: 'Enter plate' },
    plateHint:       { ar: 'مثال: أ ب ج 1234 أو ABJ 1234', en: 'Example: ABJ 1234 or أ ب ج 1234' },
    plateInvalid:    { ar: 'صيغة اللوحة غير صحيحة', en: 'Invalid plate format' },
    platePreview:    { ar: 'المعاينة', en: 'Preview' },
    vehicleInfo:     { ar: 'بيانات السيارة', en: 'Vehicle Details' },
    make:            { ar: 'الصانع', en: 'Make' },
    model:           { ar: 'الموديل', en: 'Model' },
    color:           { ar: 'اللون', en: 'Color' },
    year:            { ar: 'سنة الصنع', en: 'Year' },
    vin:             { ar: 'رقم الهيكل (VIN)', en: 'VIN' },
    notes:           { ar: 'ملاحظات', en: 'Notes' },
    active:          { ar: 'نشط', en: 'Active' },
    save:            { ar: '💾 حفظ', en: '💾 Save' },
    saving:          { ar: 'جاري الحفظ...', en: 'Saving...' },
    cancel:          { ar: 'إلغاء', en: 'Cancel' },
    plate:           { ar: 'اللوحة', en: 'Plate' },
    actions:         { ar: 'الإجراءات', en: 'Actions' },
    loading:         { ar: 'جاري التحميل...', en: 'Loading...' },
    noVehicles:      { ar: 'لا توجد سيارات. اضغط "إضافة سيارة".', en: 'No vehicles. Click "Add Vehicle".' },
    confirmDelete:   { ar: 'هل أنت متأكد من حذف هذه السيارة؟', en: 'Delete this vehicle?' },
    plateRequired:   { ar: 'رقم اللوحة مطلوب', en: 'Plate is required' },
    clientRequired:  { ar: 'العميل مطلوب', en: 'Client is required' },
    saveError:       { ar: 'فشل الحفظ', en: 'Failed to save' },
    deleteError:     { ar: 'فشل الحذف', en: 'Failed to delete' },
    duplicate:       { ar: 'هذه اللوحة مسجلة بالفعل', en: 'This plate is already registered' },
    prev:            { ar: '« السابق', en: '« Previous' },
    next:            { ar: 'التالي »', en: 'Next »' },
    page:            { ar: 'صفحة', en: 'Page' },
    of:              { ar: 'من', en: 'of' },
    filterClient:    { ar: 'كل العملاء', en: 'All clients' },
    clearClient:     { ar: 'إزالة الفلتر', en: 'Clear filter' },
  };
  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  // ===== State =====
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [pagination, setPagination] = useState({ total: 0, page: 1, total_pages: 1 });
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [platePreview, setPlatePreview] = useState({ valid: false, display_en: '', display_ar: '' });

  // ===== Searchable client dropdown state =====
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClientName, setSelectedClientName] = useState('');
  const clientDropdownRef = useRef(null);

  const emptyForm = {
    client_id: '', plate_input: '', make: '', model: '',
    color: '', color_ar: '', year: '', vin: '', notes: '', is_active: true
  };
  const [form, setForm] = useState({ ...emptyForm });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ===== Live plate preview =====
  const updatePlatePreview = useCallback(async (input) => {
    if (!input || !input.trim()) {
      setPlatePreview({ valid: false, display_en: '', display_ar: '' });
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/vehicles/normalize-plate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plate_input: input })
      });
      const data = await res.json();
      if (data.success) {
        setPlatePreview({
          valid: data.valid,
          display_en: data.display_en || '',
          display_ar: data.display_ar || '',
          letters_en: data.letters_en,
          numbers: data.numbers
        });
      }
    } catch (err) {
      console.error('[VSM] Plate preview error:', err);
    }
  }, []);

  useEffect(() => {
    if (!showForm) return;
    const handle = setTimeout(() => updatePlatePreview(form.plate_input), 300);
    return () => clearTimeout(handle);
  }, [form.plate_input, showForm, updatePlatePreview]);

  // ===== Fetch vehicles =====
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const params = new URLSearchParams({ page: pagination.page, limit: 15 });
    if (search) params.append('search', search);
    if (filterClientId) params.append('client_id', filterClientId);

    try {
      const res = await fetch(`${API}/vehicles?${params}`, { headers });
      const data = await res.json();
      if (data.success) {
        setVehicles(data.data);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (err) {
      console.error('[VSM] Fetch vehicles error:', err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, pagination.page, filterClientId]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  // ===== Fetch all clients =====
  const fetchClients = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/clients?limit=500`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setClients(data.data);
    } catch (err) {
      console.error('[VSM] Fetch clients error:', err);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // ===== Click outside to close dropdown =====
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ===== Handle URL params: auto-open form with preselected client =====
  useEffect(() => {
    const clientIdParam = searchParams.get('client_id');
    const newParam = searchParams.get('new');

    if (newParam === '1' && clientIdParam && clients.length > 0) {
      const client = clients.find(c => String(c.id) === String(clientIdParam));
      if (client) {
        resetForm();
        setForm(prev => ({ ...prev, client_id: clientIdParam }));
        setSelectedClientName(isRTL && client.name_ar ? client.name_ar : client.name);
        setShowForm(true);
        // Clear URL params after using them
        searchParams.delete('client_id');
        searchParams.delete('new');
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients, searchParams]);

  // ===== Form actions =====
  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setPlatePreview({ valid: false, display_en: '', display_ar: '' });
    setSelectedClientName('');
    setClientSearch('');
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (v, e) => {
    e?.stopPropagation();
    setForm({
      client_id: v.client_id || '',
      plate_input: v.plate_normalized || '',
      make: v.make || '',
      model: v.model || '',
      color: v.color || '',
      color_ar: v.color_ar || '',
      year: v.year || '',
      vin: v.vin || '',
      notes: v.notes || '',
      is_active: !!v.is_active
    });
    setSelectedClientName(isRTL && v.client_name_ar ? v.client_name_ar : v.client_name);
    setEditId(v.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { alert(tr('clientRequired')); return; }
    if (!form.plate_input || !form.plate_input.trim()) { alert(tr('plateRequired')); return; }
    if (!platePreview.valid) { alert(tr('plateInvalid')); return; }

    setSaving(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      const url = editId ? `${API}/vehicles/${editId}` : `${API}/vehicles`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        resetForm();
        fetchVehicles();
      } else {
        if (res.status === 409) alert(tr('duplicate'));
        else alert(data.error || tr('saveError'));
      }
    } catch (err) {
      console.error('[VSM] Save vehicle error:', err);
      alert(tr('saveError'));
    }
    setSaving(false);
  };

  const handleDelete = async (v, e) => {
    e?.stopPropagation();
    if (!window.confirm(`${tr('confirmDelete')}\n\n${v.plate_display}`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/vehicles/${v.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) fetchVehicles();
      else alert(data.error || tr('deleteError'));
    } catch (err) {
      console.error('[VSM] Delete vehicle error:', err);
      alert(tr('deleteError'));
    }
  };

  // ===== Client dropdown helpers =====
  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const s = clientSearch.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(s)) ||
      (c.name_ar && c.name_ar.includes(clientSearch)) ||
      (c.phone && c.phone.includes(s)) ||
      (c.whatsapp && c.whatsapp.includes(s))
    );
  });

  const selectClient = (c) => {
    handleChange('client_id', c.id);
    setSelectedClientName(isRTL && c.name_ar ? c.name_ar : c.name);
    setShowClientDropdown(false);
    setClientSearch('');
  };

  const clearClientSelection = () => {
    handleChange('client_id', '');
    setSelectedClientName('');
    setClientSearch('');
  };

  // ===== Filter dropdown (above table) =====
  const [filterClientName, setFilterClientName] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const filterDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredFilterClients = clients.filter(c => {
    if (!filterSearch) return true;
    const s = filterSearch.toLowerCase();
    return (
      (c.name && c.name.toLowerCase().includes(s)) ||
      (c.name_ar && c.name_ar.includes(filterSearch))
    );
  });

  const selectFilterClient = (c) => {
    setFilterClientId(c.id);
    setFilterClientName(isRTL && c.name_ar ? c.name_ar : c.name);
    setShowFilterDropdown(false);
    setFilterSearch('');
    setPagination(p => ({ ...p, page: 1 }));
  };

  const clearFilterClient = () => {
    setFilterClientId('');
    setFilterClientName('');
    setPagination(p => ({ ...p, page: 1 }));
  };

  // ===== Styles =====
  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: '11px', color: '#94a3b8', fontWeight: '600',
    display: 'block', marginBottom: '4px'
  };
  const sectionStyle = {
    background: 'rgba(15,23,42,0.3)', borderRadius: '10px',
    padding: '16px', marginBottom: '14px',
    border: '1px solid rgba(255,255,255,0.06)'
  };
  const sectionTitle = (icon, text) => (
    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#818cf8',
      margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>{icon} {text}</h3>
  );
  const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };
  const modalBox = {
    background: '#1e293b', borderRadius: '16px', padding: '24px',
    maxHeight: '90vh', overflow: 'auto',
    border: '1px solid rgba(255,255,255,0.1)',
    direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
  };
  const tableHeaderStyle = {
    padding: '10px 12px', fontSize: '12px', color: '#94a3b8',
    fontWeight: '600', textAlign: isRTL ? 'right' : 'left',
    borderBottom: '1px solid rgba(255,255,255,0.08)'
  };
  const tableCellStyle = {
    padding: '12px', fontSize: '13px', color: '#e2e8f0',
    borderBottom: '1px solid rgba(255,255,255,0.04)'
  };

  // عنصر اللوحة (preview)
  const PlatePlate = ({ display, valid }) => {
    if (!display) return null;
    const parts = display.split('  ');
    const letters = parts[0] || '';
    const numbers = parts[1] || '';
    return (
      <div style={{
        display: 'inline-block',
        background: valid ? 'linear-gradient(180deg, #fff, #e5e5e5)' : 'rgba(239,68,68,0.1)',
        border: `2px solid ${valid ? '#1a1a2e' : '#ef4444'}`,
        borderRadius: '6px',
        padding: '8px 14px',
        fontFamily: 'monospace',
        fontWeight: '900',
        fontSize: '20px',
        color: valid ? '#1a1a2e' : '#ef4444',
        letterSpacing: '2px',
        boxShadow: valid ? '0 2px 6px rgba(0,0,0,0.3)' : 'none',
        minWidth: '160px',
        textAlign: 'center'
      }}>
        <div>{letters}</div>
        <div style={{ fontSize: '24px', marginTop: '2px' }}>{numbers}</div>
      </div>
    );
  };

  // ===== Searchable client dropdown component =====
  const ClientDropdown = ({ inForm = false }) => {
    const dropdownStyle = {
      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
      background: '#1e293b', border: '1px solid rgba(255,255,255,0.15)',
      borderRadius: '8px', maxHeight: '300px', overflow: 'auto',
      zIndex: 1100, boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
    };

    const search = inForm ? clientSearch : filterSearch;
    const setSearch = inForm ? setClientSearch : setFilterSearch;
    const list = inForm ? filteredClients : filteredFilterClients;
    const onSelect = inForm ? selectClient : selectFilterClient;

    return (
      <div style={dropdownStyle}>
        <input
          autoFocus
          type="text"
          placeholder={tr('selectClient')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            width: '100%', padding: '10px 12px',
            border: 'none', borderBottom: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(15,23,42,0.5)', color: '#f9fafb',
            fontSize: '13px', outline: 'none', boxSizing: 'border-box',
            direction: isRTL ? 'rtl' : 'ltr'
          }}
          onClick={e => e.stopPropagation()}
        />
        {list.length === 0 ? (
          <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
            {tr('noClientFound')}
          </div>
        ) : (
          list.map(c => (
            <div
              key={c.id}
              onClick={() => onSelect(c)}
              style={{
                padding: '10px 14px', cursor: 'pointer',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                transition: 'background 0.1s'
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ color: '#f1f5f9', fontWeight: '600', fontSize: '13px' }}>
                {isRTL && c.name_ar ? c.name_ar : c.name}
              </div>
              {c.phone && (
                <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }} dir="ltr">
                  {c.phone}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1300px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
              {tr('title')}
            </h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>
              {pagination.total} {tr('subtitle')}
            </p>
          </div>
          <button
            onClick={openCreate}
            style={{
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
              border: 'none', padding: '9px 18px', borderRadius: '8px',
              fontWeight: '600', fontSize: '13px', cursor: 'pointer'
            }}
          >
            {tr('add')}
          </button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <input
            type="text"
            placeholder={tr('search')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            style={{ ...inputStyle, maxWidth: '300px' }}
          />

          {/* Searchable filter dropdown */}
          <div ref={filterDropdownRef} style={{ position: 'relative', maxWidth: '280px', width: '100%' }}>
            <div
              onClick={() => setShowFilterDropdown(s => !s)}
              style={{
                ...inputStyle, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '8px'
              }}
            >
              <span style={{ color: filterClientName ? '#f9fafb' : '#64748b', fontSize: '13px' }}>
                {filterClientName || tr('filterClient')}
              </span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {filterClientName && (
                  <span
                    onClick={(e) => { e.stopPropagation(); clearFilterClient(); }}
                    style={{ color: '#fca5a5', fontSize: '14px', cursor: 'pointer', padding: '0 4px' }}
                  >
                    ✕
                  </span>
                )}
                <span style={{ color: '#94a3b8', fontSize: '10px' }}>▼</span>
              </div>
            </div>
            {showFilterDropdown && <ClientDropdown inForm={false} />}
          </div>
        </div>

        {/* Table */}
        <div style={{
          background: 'rgba(30,41,59,0.5)', borderRadius: '12px',
          overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)'
        }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{tr('loading')}</div>
          ) : vehicles.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>{tr('noVehicles')}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'rgba(15,23,42,0.5)' }}>
                <tr>
                  <th style={tableHeaderStyle}>{tr('plate')}</th>
                  <th style={tableHeaderStyle}>{tr('client')}</th>
                  <th style={tableHeaderStyle}>{tr('make')} / {tr('model')}</th>
                  <th style={tableHeaderStyle}>{tr('color')}</th>
                  <th style={tableHeaderStyle}>{tr('year')}</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>{tr('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map(v => (
                  <tr key={v.id} style={{ transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={tableCellStyle}>
                      <div style={{
                        display: 'inline-block',
                        background: 'linear-gradient(180deg, #fff, #e5e5e5)',
                        border: '2px solid #1a1a2e',
                        borderRadius: '4px',
                        padding: '4px 10px',
                        fontFamily: 'monospace',
                        fontWeight: '900',
                        fontSize: '14px',
                        color: '#1a1a2e',
                        letterSpacing: '1px',
                        minWidth: '120px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '11px' }}>{v.plate_letters_en} / {v.plate_letters_ar}</div>
                        <div style={{ fontSize: '15px' }}>{v.plate_numbers}</div>
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: '600' }}>
                        {isRTL && v.client_name_ar ? v.client_name_ar : v.client_name}
                      </div>
                      {v.client_phone && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }} dir="ltr">
                          {v.client_phone}
                        </div>
                      )}
                    </td>
                    <td style={tableCellStyle}>
                      {v.make || '—'} {v.model && `/ ${v.model}`}
                    </td>
                    <td style={tableCellStyle}>
                      {isRTL && v.color_ar ? v.color_ar : v.color || '—'}
                    </td>
                    <td style={tableCellStyle}>{v.year || '—'}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button
                          onClick={(e) => openEdit(v, e)}
                          style={{
                            background: 'rgba(99,102,241,0.2)', color: '#a5b4fc',
                            border: '1px solid rgba(99,102,241,0.3)',
                            padding: '5px 10px', borderRadius: '6px',
                            fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                          }}
                        >✏️</button>
                        <button
                          onClick={(e) => handleDelete(v, e)}
                          style={{
                            background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                            border: '1px solid rgba(239,68,68,0.25)',
                            padding: '5px 10px', borderRadius: '6px',
                            fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                          }}
                        >🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
            <button
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              style={{
                background: pagination.page <= 1 ? 'rgba(100,116,139,0.2)' : 'rgba(99,102,241,0.2)',
                color: pagination.page <= 1 ? '#64748b' : '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
              }}
            >{tr('prev')}</button>
            <span style={{ color: '#94a3b8', fontSize: '13px' }}>
              {tr('page')} {pagination.page} {tr('of')} {pagination.total_pages}
            </span>
            <button
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              style={{
                background: pagination.page >= pagination.total_pages ? 'rgba(100,116,139,0.2)' : 'rgba(99,102,241,0.2)',
                color: pagination.page >= pagination.total_pages ? '#64748b' : '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.3)',
                padding: '8px 16px', borderRadius: '8px',
                fontSize: '13px', cursor: pagination.page >= pagination.total_pages ? 'not-allowed' : 'pointer'
              }}
            >{tr('next')}</button>
          </div>
        )}

        {/* Vehicle Form Modal */}
        {showForm && (
          <div style={modalOverlay} onClick={() => setShowForm(false)}>
            <div style={{ ...modalBox, width: '700px', maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 16px' }}>
                {editId ? tr('edit') : tr('create')}
              </h2>
              <form onSubmit={handleSubmit}>

                {/* Searchable Client Dropdown */}
                <div style={sectionStyle}>
                  {sectionTitle('👤', tr('client'))}
                  <div ref={clientDropdownRef} style={{ position: 'relative' }}>
                    <div
                      onClick={() => setShowClientDropdown(s => !s)}
                      style={{
                        ...inputStyle, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: '8px'
                      }}
                    >
                      <span style={{ color: selectedClientName ? '#f9fafb' : '#64748b' }}>
                        {selectedClientName || tr('selectClient')}
                      </span>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        {selectedClientName && (
                          <span
                            onClick={(e) => { e.stopPropagation(); clearClientSelection(); }}
                            style={{ color: '#fca5a5', fontSize: '14px', cursor: 'pointer', padding: '0 4px' }}
                          >
                            ✕
                          </span>
                        )}
                        <span style={{ color: '#94a3b8', fontSize: '10px' }}>▼</span>
                      </div>
                    </div>
                    {showClientDropdown && <ClientDropdown inForm={true} />}
                  </div>
                </div>

                {/* Plate */}
                <div style={sectionStyle}>
                  {sectionTitle('🔢', tr('plateInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
                    <div>
                      <label style={labelStyle}>{tr('plateInput')} *</label>
                      <input
                        value={form.plate_input}
                        onChange={(e) => handleChange('plate_input', e.target.value)}
                        style={{
                          ...inputStyle,
                          fontSize: '18px', fontFamily: 'monospace',
                          fontWeight: '700', letterSpacing: '2px', textAlign: 'center'
                        }}
                        placeholder="ABJ 1234"
                        required
                      />
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px' }}>
                        {tr('plateHint')}
                      </div>
                      {form.plate_input && !platePreview.valid && (
                        <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '6px' }}>
                          ⚠️ {tr('plateInvalid')}
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>{tr('platePreview')}</label>
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        minHeight: '70px', background: 'rgba(15,23,42,0.5)',
                        borderRadius: '8px', padding: '8px'
                      }}>
                        {platePreview.valid ? (
                          <PlatePlate display={isRTL ? platePreview.display_ar : platePreview.display_en} valid={true} />
                        ) : (
                          <span style={{ color: '#64748b', fontSize: '12px' }}>—</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Vehicle details */}
                <div style={sectionStyle}>
                  {sectionTitle('🚗', tr('vehicleInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('make')}</label>
                      <input value={form.make} onChange={e => handleChange('make', e.target.value)} style={inputStyle} placeholder="Toyota" />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('model')}</label>
                      <input value={form.model} onChange={e => handleChange('model', e.target.value)} style={inputStyle} placeholder="Camry" />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('year')}</label>
                      <input type="number" min="1950" max="2099" value={form.year} onChange={e => handleChange('year', e.target.value)} style={inputStyle} placeholder="2024" />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('color')} (EN)</label>
                      <input value={form.color} onChange={e => handleChange('color', e.target.value)} style={inputStyle} placeholder="White" />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('color')} (AR)</label>
                      <input value={form.color_ar} onChange={e => handleChange('color_ar', e.target.value)} style={{ ...inputStyle, textAlign: 'right' }} dir="rtl" placeholder="أبيض" />
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>{tr('vin')}</label>
                    <input value={form.vin} onChange={e => handleChange('vin', e.target.value)} style={{ ...inputStyle, fontFamily: 'monospace' }} dir="ltr" />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={labelStyle}>{tr('notes')}</label>
                    <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} rows={2} />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#cbd5e1' }}>
                      <input type="checkbox" checked={form.is_active} onChange={e => handleChange('is_active', e.target.checked)} style={{ width: '16px', height: '16px' }} />
                      {tr('active')}
                    </label>
                  </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    style={{
                      background: 'rgba(100,116,139,0.2)', color: '#cbd5e1',
                      border: '1px solid rgba(100,116,139,0.3)',
                      padding: '10px 20px', borderRadius: '8px',
                      fontSize: '13px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >{tr('cancel')}</button>
                  <button
                    type="submit"
                    disabled={saving || !platePreview.valid}
                    style={{
                      background: (saving || !platePreview.valid) ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      color: '#fff', border: 'none',
                      padding: '10px 24px', borderRadius: '8px',
                      fontSize: '13px', cursor: (saving || !platePreview.valid) ? 'not-allowed' : 'pointer',
                      fontWeight: '700'
                    }}
                  >{saving ? tr('saving') : tr('save')}</button>
                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default VSMVehicles;
